<template>
  <div
    v-on-clickaway="close"
    :class="classes"
  >
    <div class="select__field-wrapper" @click="onActivate">
      <label
        v-if="label"
        :for="id"
        class="select__label"
      >
        {{ label }}
        <span v-if="required" class="select__label-required">*</span>
      </label>

      <div class="select__field">
        <div class="select__text">
          {{ text }}
        </div>
        <ChevronIcon v-if="showArrow && itemsWithoutDefault.length >= 1 " class="select__arrow" />
      </div>
    </div>

    <div :class="['select__dropdown', `select__dropdown_scrollbar_${scrollbar}`]">
      <div
        v-for="(item, index) in filteredItems"
        :key="`select-item-${index}-in-${id}`"
        class="select__dropdown-item"
        @click="onItemSelect(item,true)"
      >
        {{ item[itemTextKey] }}
      </div>
    </div>

    <transition name="slide-from-top">
      <span
        v-if="error && errorMsg"
        class="select__error-msg"
      >
        {{ errorMsg }}
      </span>
    </transition>
  </div>
</template>

<script>
  import ChevronIcon from '@/assets/img/icons/chevron.svg?inline'
  import { mixin as clickaway } from 'vue-clickaway'
  import { get } from 'lodash'

  export default {
    name: 'BaseSelect',
    components: {
      ChevronIcon
    },
    mixins: [ clickaway ],
    props: {
      value: {
        type: null,
        default: ''
      },
      required: {
        type: Boolean,
        default: false
      },
      disableAutoSelect: {
        type: Boolean,
        default: false
      },
      showArrow: {
        type: Boolean,
        default: true
      },
      label: {
        type: String,
        default: ''
      },
      placeholder: {
        type: String,
        default: ''
      },
      items: {
        type: Array,
        requied: true
      },
      itemValueKey: {
        type: String,
        default: 'value'
      },
      itemTextKey: {
        type: String,
        default: 'text'
      },
      errorMsg: {
        type: String,
        default: ''
      },
      scrollbar: {
        type: String,
        default: 'custom'
      },
      desabled: {
        type: String,
        default: ''
      }
    },
    data () {
      return {
        selected: null,
        isActive: false,
        isInitialized: false,
        error: null
      }
    },
    computed: {
      id () {
        return `select-${this._uid}`
      },
      text () {
        return get(this, ['selected', this.itemTextKey]) || this.placeholder
      },
      classes () {
        return [
          'select',
          {
            'select_active': this.isActive,
            'select_error': this.error,
            'select_no-items': this.items.length < 2
          }
        ]
      },
      filteredItems () {
        return this.items.filter(item => item.value !== get(this, 'selected.value'))
      },
      itemsWithoutDefault () {
        return this.items.filter(item => item.value !== null && item.value !== get(this, 'selected.value'))
      }
    },
    watch: {
      items: {
        immediate: true,
        handler (newVal) {
          if (this.disableAutoSelect) return
          if (this.items.filter((item) => item.value).length === 1 && this.items.filter((item) => item.value)[0].value) {
            setTimeout(() => {
              this.onItemSelect(this.items.filter((item) => item.value)[0])
            })
          }
        }
      },
      value: {
        handler (v) {
          const filetedItems = this.items.filter(item => item[this.itemValueKey] === this.value)
          this.onItemSelect(filetedItems.length && filetedItems[0].value
            ? filetedItems[0]
            : this.placeholder || this.items[0] // placeholder or first
          )
          this.isInitialized = true
        },
        immediate: true
      },
      errorMsg: {
        handler (msg) {
          this.error = !!msg
        },
        immediate: true
      }
    },
    methods: {
      onActivate () {
        if (this.desabled || (this.showArrow && this.itemsWithoutDefault.length <= 0)) return

        this.isActive = !this.isActive
        this.error = false
      },
      onItemSelect (item) {
        this.selected = item
        if (this.isInitialized) {
          this.$emit('input', this.selected[this.itemValueKey], this.colIndex)
        }
        this.close()
      },
      close () {
        this.isActive = false
      }
    }
  }
</script>

<style lang="scss">
  $padding-for-error: 28px;
  $active-box-shadow: 2px 2px 5px rgba($c-black, 0.1);

  .select {
    position: relative;
    display: block;
    min-width: 290px;
    max-width: 335px;
    padding-bottom: $padding-for-error;
    cursor: pointer;

    @include mobile {
      max-width: 100%;
    }

    &__label {
      display: inline-block;
      padding-bottom: 1em;
      font-size: 16px;
      font-weight: 300;
      color: $c-input-grey;

      &-required {
        color: $c-active-light;
      }
    }

    &__text {
      padding-right: 20px;
      font-size: 15px;
      text-overflow: ellipsis;
      overflow: hidden;
    }

    &__field {
      position: relative;
      display: block;
      width: 100%;
      min-height: 46px;
      padding: 0.938em 1.3em;
      font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;
      font-size: 16px;
      line-height: 1;
      background-color: $c-gray-light;
      border-radius: 4px;
      outline: none;
      caret-color: $c-active-light;
      transition: 0.3s;

      &:focus {
        background-color: $c-gray;
      }

      @include hover {
        background-color: $c-gray;
      }
    }

    &__arrow {
      position: absolute;
      right: 4%;
      bottom: 35%;
      width: 13px;
      height: 13px;
      stroke: $c-active-light;
      transition: 0.3s;
      transform: rotate(-90deg);
    }

    &__dropdown {
      position: absolute;
      top: calc(100% - #{$padding-for-error});
      left: 0;
      z-index: 999;
      width: 100%;
      max-height: 270px;
      overflow-y: auto;
      font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;
      visibility: hidden;
      border-bottom-right-radius: 4px;
      border-bottom-left-radius: 4px;
      box-shadow: $active-box-shadow;
      opacity: 0;
      transition: 0.3s;
      -webkit-overflow-scrolling: touch;

      &_scrollbar {
        &_custom {
          @include custom-scrollbar();
        }

        &_none {
          @include no-scrollbar();
        }
      }

      &-item {
        padding: 0.938em 1.3em;
        font-size: 15px;
        line-height: 1;
        color: #333;
        background-color: $c-gray-light;
        border-top: 1px solid rgba(0, 0, 0, 0.05);
        transition: background-color 0.3s;

        @include hover {
          background-color: $c-gray;
        }
      }
    }

    &__error-msg {
      position: absolute;
      top: 77%;
      left: 0;
      z-index: 1;
      font-size: 12px;
      color: $c-active-light;
    }

    &_error {
      .select {
        &__field {
          background-color: $c-error-light;
        }
      }
    }

    &_no-items {
      pointer-events: none;
    }

    &_active {
      .select {
        &__field {
          background-color: $c-gray;
          border-bottom-right-radius: 0;
          border-bottom-left-radius: 0;
          box-shadow: $active-box-shadow;
        }

        &__arrow {
          transform: rotate(-270deg);
        }

        &__dropdown {
          visibility: visible;
          opacity: 1;
        }
      }
    }
  }
</style>
